import { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import FormDrawer from "components/__new/common/form-drawer";
import { ReactComponent as IconInfo } from 'assets/images/icons/project-icons/information-new.svg'
import { ReactComponent as IconCaution } from "assets/images/icons/caution.svg";
import Maybe from "../common/Maybe";
import { ButtonLoader, Input, SelectInput } from "../common/form";
import validate from "utils/validate";
import useLoginInfo from "hooks/useLoginInfo";
import successGif from "assets/images/icons/success-green.gif";
import * as walletAction from "store/entities/wallet/action";
import * as notificationAction from "store/entities/notification/action";
import * as dashboardActions from "store/entities/dashboard/action";
import ModalAlert from "../common/ModalAlert";
import { Menu } from "@headlessui/react";
import { ReactComponent as IconChevronDown } from 'assets/images/icons/chevron-down.svg';
import { useOutsideClick } from 'hooks/useOutsideClick';

const ACCOUNT_TYPES = [
    {
        label: "Expense Account",
        value: "expense",
        subText: "Can receive and transfer money"
    },
    {
        label: "Collection Account",
        value: "collection",
        subText: "Can only receive and move money to the main account"
    },
]

const CreateAccount = (props) => {
    const dispatch = useDispatch();
    const {userInfo} = useLoginInfo();
    const { additionalWalletLoading, additionalWallet } = useSelector((state) => state.entities.wallet);
    const {isOpen, setIsOpen, createdAccounts} = props;
    const [isFormValidated, setIsFormValidated] = useState(true);
    const [isOpenModalAlertSuccess, setIsOpenModalAlertSuccess] = useState(false);
    const [form, setForm] = useState({
        walletName: "",
        walletType: "",
    })
    const accountTypeDropdownRef = useRef(null);
    const [isAccountTypeDropdownOpen, setIsAccountTypeDropdownOpen] = useState(false);
    const [selectedAccountType, setSelectedAccountType] = useState(null);

    const disableBtn = !form?.walletName?.length || !form?.walletType?.length;
    
    const handleToggleAccountTypeDropdown = () => {
        setIsAccountTypeDropdownOpen(!isAccountTypeDropdownOpen);
    }
    
    useOutsideClick(accountTypeDropdownRef, () => {
        if (isAccountTypeDropdownOpen) {
            handleToggleAccountTypeDropdown();
        }
    });
    
    const checkFormValidation = (data = form, appendKey, appendValue) => {
        let params = {...data};
        if (appendKey){ params = {...params, [appendKey]: appendValue}; }
        const errors = validate(params);
        if (errors){
            setIsFormValidated(false);
        }
        else{
            setIsFormValidated(true);
        }
    }
    
    const isSubmitBtnDisabled = useMemo(() => {
        return !form.walletName || !form.walletType;
    }, [form]);
    
    const handleCloseCreateAccountModal = () => {
        setIsAccountTypeDropdownOpen(false);
        setSelectedAccountType(null);
        setIsOpen(false);
        setForm({
            walletName: "",
            walletType: "",
        });
    }

    const submitForm = async (e) => {
        e.preventDefault();
        const errors = validate(form);
        if (errors){
            for (var x in errors) {
                toast.error(errors[x]);
            };
            return;
        }
        else{
            const payload = {
                walletType: form.walletType,
                walletName: form.walletName
            }
            dispatch(walletAction.createAdditionalWallet(payload))
        }
    }

    useEffect(() => {
        checkFormValidation();
        // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (additionalWallet?.status === "success") {
            handleCloseCreateAccountModal();
            setIsOpenModalAlertSuccess(true);
            dispatch(walletAction.getBusinessAccounts());
            dispatch(walletAction.resetCreateAdditionalWallet());
            dispatch(notificationAction.getAllNotifications());
        }
    }, [additionalWallet, dispatch]);

    return (
        <>
            <ModalAlert
                successText="Account Created Successfully"
                dismissText="Close"
                isOpenModalAlert={isOpenModalAlertSuccess}
                setIsOpenModalAlert={setIsOpenModalAlertSuccess}
            />

            <Maybe condition={createdAccounts !== 10}>
                <FormDrawer display="center" size="lg" isOpen={isOpen} setIsOpen={handleCloseCreateAccountModal}>
                    <div className="p-6">
                        <FormDrawer.Title
                            title="Create New Account"
                            description="Add a new account for your business"
                            showCloseButton={true}
                            close={handleCloseCreateAccountModal}
                        />

                        <hr className="w-full h-px mt-2 bg-[#E2E4E8] border-none" />

                        <form onSubmit={submitForm}>
                            <div className="mt-4">
                                <Input
                                    type="text"
                                    label="Account Name"
                                    placeholder="Enter a name"
                                    value={form.walletName || ""}
                                    onChange={(e) => setForm({...form, walletName: e.target.value})}
                                    validationName="walletName"
                                    showCharacterLength={true}
                                    maxLength={50}
                                    characterCount={form?.walletName?.length}
                                    displayCharacterCountInside={true}
                                    checkFormValidation={checkFormValidation}
                                />
                                <Menu as="div" className="relative form-group select-account-type-menu">
                                    <label>Account Type 
                                        <span className="form-input-required">*</span>
                                    </label>
                                    <div>
                                        <Menu.Button
                                            onClick={handleToggleAccountTypeDropdown} 
                                            className="select-input flex items-center justify-between"
                                        >
                                            {
                                                selectedAccountType ? (
                                                    <span>{selectedAccountType?.label || "-"}</span>
                                                ) : (
                                                    <span>Select an account type</span>
                                                )
                                            }
                                            <IconChevronDown />
                                        </Menu.Button>
                                    </div>
                                   {
                                        isAccountTypeDropdownOpen && (
                                            <Menu.Items 
                                                as="div" 
                                                static 
                                                className="dropdown dropdown-right top-12 w-full rounded-lg"
                                                style={{
                                                    boxShadow: "0px 12px 16px -4px #10182814, 0px -12px 16px -4px #10182814"
                                                }}
                                                ref={accountTypeDropdownRef}
                                            >
                                                {
                                                    ACCOUNT_TYPES.map((accountType, atix) => (
                                                        <Menu.Item 
                                                            as="div" 
                                                            className="w-full text-sm cursor-pointer account-type-item" 
                                                            key={`${atix}-${accountType.value}`}
                                                            onClick={() => {
                                                                setForm({
                                                                    ...form, 
                                                                    walletType: accountType.value
                                                                });
                                                                setSelectedAccountType(accountType);
                                                                handleToggleAccountTypeDropdown();
                                                            }}
                                                        >
                                                            <p
                                                                className="text-base font-medium"
                                                                style={{
                                                                    color: "#1A202C"
                                                                }}
                                                            >
                                                                {accountType.label}
                                                            </p>
                                                            <p 
                                                                className="text-sm"
                                                                style={{
                                                                    color: "#4A5564"
                                                                }}
                                                            >
                                                                {accountType.subText}
                                                            </p>
                                                        </Menu.Item>
                                                    ))
                                                }
                                            </Menu.Items>
                                        )
                                   }
                                </Menu>

                                <div className="flex items-center bg-[#FEF5E7] p-3 rounded-full">
                                    <div className="mr-2"><IconInfo /></div>
                                    <div className="text-[14px]">You can only create a maximum of <span className="font-medium">{10}</span> accounts</div>
                                </div>

                                <div className="mt-5 flex items-center justify-end space-x-3">
                                    <div>
                                        <button type="submit" className="btn btn-lg w-full rounded-lg whitespace-nowrap border border-ep-primary-600 text-ep-primary-600 bg-white"
                                            onClick={handleCloseCreateAccountModal}
                                            >
                                            Cancel
                                        </button>
                                    </div>
                                    <div>
                                        <ButtonLoader disabled={disableBtn || isSubmitBtnDisabled} type="submit" loading={additionalWalletLoading} className="btn btn-lg w-full rounded-lg whitespace-nowrap text-white border border-ep-primary-600 bg-ep-primary-600"
                                        >
                                            Create
                                        </ButtonLoader>
                                    </div>

                                </div>
                            </div>
                        </form>
                    </div>
                </FormDrawer>
            </Maybe>

            {/* Display this modal if account has already reached 10 */}
            <Maybe condition={createdAccounts === 10}>
                <FormDrawer display="center" size="lg" isOpen={isOpen} setIsOpen={setIsOpen}>
                    <div className="p-6">
                        <div className="flex justify-center">
                            <IconCaution />
                        </div>

                        <div className="mt-4 py-2">
                            <div className="text-center text-[#4A5564] font-normal text-lg">
                                You've reached your limit. <br /> Contact <span className="text-ep-primary-600">business@earnipay.com</span> to <br /> create more accounts
                            </div>
                        </div>

                        <div className="py-2 mt-4 flex justify-center gap-6">
                            <button type="button" className="btn btn-lg btn-block rounded-lg whitespace-nowrap text-white bg-ep-primary-600" onClick={() => setIsOpen(false)}>
                                Close
                            </button>
                        </div>
                    </div>
                </FormDrawer>
            </Maybe>
        </>
    )
}


export default CreateAccount;